// hooks/usePendingKnowledgeCount.js
import { useState, useEffect } from 'react'
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  getCountFromServer
} from 'firebase/firestore'
import { app } from '../firebase'
import UserService from '../services/UserService'

/**
 * Custom Hook to get real-time count of pending knowledge
 * Works for moderation-capable roles
 *
 * @param {Object} user - Current user object with uid
 * @param {string} userRole - User's role ('admin', 'superadmin', etc.)
 * @returns {number} count - Number of pending knowledge items
 */
export function usePendingKnowledgeCount(user, userRole) {
  const [count, setCount] = useState(0)
  const db = getFirestore(app)

  useEffect(() => {
    // Only run for roles that can moderate knowledge
    if (
      !user ||
      !userRole ||
      !['moderator', 'admin', 'superadmin'].includes(userRole)
    ) {
      setCount(0)
      return
    }

    let unsubscribe = () => {}

    const setupListener = async () => {
      try {
        const companyId = await UserService.getUserCompanyId(user.uid)

        if (!companyId) {
          setCount(0)
          return
        }

        // Real-time listener for pending knowledge
        const pendingQuery = query(
          collection(db, 'companies', companyId, 'knowledge'),
          where('status', '==', 'pending')
        )

        // Use onSnapshot for real-time updates
        unsubscribe = onSnapshot(
          pendingQuery,
          snapshot => {
            setCount(snapshot.size)
          },
          error => {
            console.error('Error listening to pending knowledge:', error)
            setCount(0)
          }
        )
      } catch (error) {
        console.error('Error setting up pending knowledge listener:', error)
        setCount(0)
      }
    }

    setupListener()

    return () => {
      unsubscribe()
    }
  }, [user, userRole, db])

  return count
}

export default usePendingKnowledgeCount
